import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, AlertCircle, CheckCircle } from 'lucide-react';

const TermsOfService: React.FC = () => {
  return (
    <div className="pt-40 pb-32 container mx-auto px-6 max-w-4xl">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-20"
      >
        <div className="inline-flex items-center space-x-2 bg-zinc-900 border border-white/10 px-4 py-1.5 rounded-full mb-8">
          <FileText className="w-3 h-3 text-accent" />
          <span className="text-xs font-bold tracking-widest text-zinc-500 uppercase">Legal Documentation</span>
        </div>
        <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 tracking-tighter leading-none">
          Terms of <span className="text-accent">Service</span>
        </h1>
        <p className="text-zinc-400 text-lg">
          Last Updated: January 23, 2026
        </p>
      </motion.div>

      {/* Introduction */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-zinc-900/30 border border-white/5 rounded-3xl p-8 mb-12"
      >
        <div className="flex items-start space-x-4">
          <AlertCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-xl font-bold mb-3">Important Notice</h3>
            <p className="text-zinc-400 leading-relaxed">
              Please read these Terms of Service carefully before using our services. By accessing or using DuoStack Systems' services, you agree to be bound by these terms.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Terms Sections */}
      <div className="space-y-12">
        <Section 
          number="01"
          title="Acceptance of Terms"
          content={
            <>
              <p>By accessing and using DuoStack Systems' services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
              <p>These Terms of Service apply to all users of the site, including without limitation users who are browsers, vendors, customers, merchants, and/or contributors of content.</p>
            </>
          }
        />

        <Section 
          number="02"
          title="Services Provided"
          content={
            <>
              <p>DuoStack Systems provides software development services including but not limited to:</p>
              <ul className="list-none space-y-2 ml-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                  <span>Custom software development and enterprise solutions</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                  <span>Mobile application development (iOS and Android)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                  <span>Frontend and backend development services</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                  <span>Database architecture and optimization</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                  <span>Intelligent automation and bot development</span>
                </li>
              </ul>
            </>
          }
        />

        <Section 
          number="03"
          title="Client Responsibilities"
          content={
            <>
              <p>As a client of DuoStack Systems, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Provide accurate and complete information necessary for project execution</li>
                <li>Respond to requests for feedback and approvals in a timely manner</li>
                <li>Make payments according to the agreed-upon schedule</li>
                <li>Not use our services for any illegal or unauthorized purpose</li>
                <li>Comply with all applicable local, state, national, and international laws and regulations</li>
              </ul>
            </>
          }
        />

        <Section 
          number="04"
          title="Intellectual Property Rights"
          content={
            <>
              <p>Upon full payment of all fees, the client shall own all rights to the final deliverables created specifically for their project. However:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>DuoStack Systems retains ownership of all pre-existing intellectual property, tools, frameworks, and methodologies</li>
                <li>Any reusable code libraries, frameworks, or components developed remain the property of DuoStack Systems unless otherwise agreed in writing</li>
                <li>DuoStack Systems reserves the right to showcase completed projects in our portfolio unless a non-disclosure agreement states otherwise</li>
              </ul>
            </>
          }
        />

        <Section 
          number="05"
          title="Payment Terms"
          content={
            <>
              <p>Unless otherwise specified in a written agreement:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Project deposits are non-refundable and due before work commences</li>
                <li>Invoices are payable within 15 days of receipt</li>
                <li>Late payments may incur additional fees and may result in suspension of services</li>
                <li>All prices are quoted in USD unless otherwise stated</li>
                <li>Clients are responsible for any transaction fees, taxes, or duties</li>
              </ul>
            </>
          }
        />

        <Section 
          number="06"
          title="Project Timelines"
          content={
            <>
              <p>Project timelines are estimates based on the information provided at the time of proposal. Actual timelines may vary based on:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Timely provision of required materials and feedback from the client</li>
                <li>Changes in project scope or requirements</li>
                <li>Technical challenges or third-party dependencies</li>
                <li>Force majeure events beyond our reasonable control</li>
              </ul>
            </>
          }
        />

        <Section 
          number="07"
          title="Warranties and Disclaimers"
          content={
            <>
              <p>DuoStack Systems warrants that:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Services will be performed in a professional and workmanlike manner</li>
                <li>Deliverables will substantially conform to specifications agreed upon in writing</li>
              </ul>
              <p className="mt-4">EXCEPT AS EXPRESSLY PROVIDED ABOVE, DUOSTACK SYSTEMS MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.</p>
            </>
          }
        />

        <Section 
          number="08"
          title="Limitation of Liability"
          content={
            <>
              <p>To the maximum extent permitted by law, DuoStack Systems shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>Your use or inability to use our services</li>
                <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
                <li>Any bugs, viruses, trojan horses, or the like that may be transmitted to or through our services by any third party</li>
              </ul>
              <p className="mt-4">Our total liability shall not exceed the amount paid by the client for the specific service giving rise to the claim.</p>
            </>
          }
        />

        <Section 
          number="09"
          title="Confidentiality"
          content={
            <>
              <p>DuoStack Systems agrees to maintain the confidentiality of all proprietary information received from clients during the course of providing services. We will not disclose such information to third parties without prior written consent, except as required by law.</p>
              <p>This obligation survives the termination of our service agreement.</p>
            </>
          }
        />

        <Section 
          number="10"
          title="Termination"
          content={
            <>
              <p>Either party may terminate a project agreement with written notice if:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-400">
                <li>The other party breaches any material term and fails to cure within 15 days of written notice</li>
                <li>The other party becomes insolvent or files for bankruptcy</li>
              </ul>
              <p className="mt-4">Upon termination, the client shall pay for all services rendered up to the termination date and any expenses incurred.</p>
            </>
          }
        />

        <Section 
          number="11"
          title="Modifications to Terms"
          content={
            <>
              <p>DuoStack Systems reserves the right to modify these Terms of Service at any time. We will notify clients of any material changes via email or through our website.</p>
              <p>Continued use of our services after such modifications constitutes acceptance of the updated terms.</p>
            </>
          }
        />

        <Section 
          number="12"
          title="Governing Law"
          content={
            <>
              <p>These Terms of Service shall be governed by and construed in accordance with the laws of Ghana, without regard to its conflict of law provisions.</p>
              <p>Any disputes arising from these terms shall be resolved through binding arbitration in Accra, Ghana, unless otherwise agreed in writing.</p>
            </>
          }
        />
      </div>

      {/* Contact Footer */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-20 bg-zinc-900/30 border border-white/5 rounded-3xl p-8"
      >
        <h3 className="text-xl font-bold mb-4">Questions About These Terms?</h3>
        <p className="text-zinc-400 mb-6">
          If you have any questions about these Terms of Service, please contact us:
        </p>
        <div className="space-y-2 text-zinc-400">
          <p>Email: <a href="mailto:legal@duostacksystems.com" className="text-accent hover:underline">legal@duostacksystems.com</a></p>
          <p>Location: Accra, Ghana</p>
        </div>
      </motion.div>
    </div>
  );
};

const Section = ({ number, title, content }: { number: string, title: string, content: React.ReactNode }) => {
  return (
    <motion.section 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="border-l-2 border-zinc-800 pl-8 hover:border-accent/50 transition-colors"
    >
      <div className="flex items-baseline space-x-4 mb-4">
        <span className="text-xs font-mono font-bold text-zinc-600">{number}</span>
        <h2 className="text-2xl font-display font-bold">{title}</h2>
      </div>
      <div className="space-y-4 text-zinc-400 leading-relaxed">
        {content}
      </div>
    </motion.section>
  );
};

export default TermsOfService;